<script setup>
/*
 * Container do Lura Copilot. Substitui o CopilotContainer do Captain: mesmo slot visual (painel
 * lateral direito), mas conversa com o backend proprio (agents.lurahosting.net) e usa o contexto
 * da conversa aberta automaticamente.
 */
import { computed, onBeforeUnmount, watch } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useWindowSize } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import wootConstants from 'dashboard/constants/globals';
import LuraCopilot from 'dashboard/components-next/copilot/LuraCopilot.vue';

const store = useStore();
const { uiSettings, updateUISettings } = useUISettings();
const { width: windowWidth } = useWindowSize();

const currentChat = useMapGetter('getSelectedChat');
const currentAccountId = useMapGetter('getCurrentAccountId');

const isSmallScreen = computed(
  () => windowWidth.value < wootConstants.SMALL_SCREEN_BREAKPOINT
);
const isPanelOpen = computed(() => !!uiSettings.value?.is_copilot_panel_open);
const conversationId = computed(() => currentChat.value?.id || null);
const conversationInboxType = computed(
  () => currentChat.value?.meta?.channel || ''
);

const thread = computed(() =>
  conversationId.value
    ? store.getters['luraCopilot/getThread'](conversationId.value)
    : null
);
const activeRun = computed(() =>
  conversationId.value
    ? store.getters['luraCopilot/getActiveRun'](conversationId.value)
    : null
);

let subscribedConversationId = null;

const detach = () => {
  if (!subscribedConversationId) return;
  store.dispatch('luraCopilot/unsubscribe', {
    conversationId: subscribedConversationId,
  });
  subscribedConversationId = null;
};

const attach = async id => {
  if (!id || subscribedConversationId === id) return;
  detach();
  subscribedConversationId = id;
  const accountId = currentAccountId.value;
  await store.dispatch('luraCopilot/load', { accountId, conversationId: id });
  store.dispatch('luraCopilot/subscribe', { accountId, conversationId: id });
};

watch(
  [isPanelOpen, conversationId],
  ([open, id]) => {
    if (open && id) attach(id);
    else detach();
  },
  { immediate: true }
);

onBeforeUnmount(detach);

const closeCopilotPanel = () => {
  if (isSmallScreen.value && isPanelOpen.value) {
    updateUISettings({
      is_contact_sidebar_open: false,
      is_copilot_panel_open: false,
    });
  }
};

const sendMessage = async message => {
  if (!conversationId.value) return;
  if (activeRun.value) {
    useAlert('Aguarde o Helix terminar a execução atual (ou cancele).');
    return;
  }
  try {
    await store.dispatch('luraCopilot/send', {
      accountId: currentAccountId.value,
      conversationId: conversationId.value,
      message,
    });
  } catch (error) {
    useAlert(error.message || 'Não foi possível enviar para o Helix.');
  }
};

const decide = async ({ runId, callId, approved, always }) => {
  try {
    await store.dispatch('luraCopilot/approve', {
      conversationId: conversationId.value,
      runId,
      callId,
      approved,
      always,
    });
  } catch (error) {
    useAlert(error.message || 'Não foi possível registrar a decisão.');
  }
};

const cancelRun = async runId => {
  try {
    await store.dispatch('luraCopilot/cancel', { runId });
  } catch (error) {
    useAlert(error.message || 'Não foi possível cancelar.');
  }
};

const setAutoApprove = value => {
  store.dispatch('luraCopilot/setAutoApprove', {
    conversationId: conversationId.value,
    value,
  });
};

const resetThread = async () => {
  try {
    await store.dispatch('luraCopilot/reset', {
      accountId: currentAccountId.value,
      conversationId: conversationId.value,
    });
  } catch (error) {
    useAlert(error.message || 'Não foi possível limpar a conversa.');
  }
};
</script>

<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<template>
  <div
    v-if="isPanelOpen"
    v-on-click-outside="() => closeCopilotPanel()"
    class="bg-n-surface-2 h-full overflow-hidden flex-col fixed top-0 ltr:right-0 rtl:left-0 z-40 w-full max-w-sm transition-transform duration-300 ease-in-out md:static md:w-[340px] md:min-w-[340px] ltr:border-l rtl:border-r border-n-weak 2xl:min-w-[380px] 2xl:w-[380px] shadow-lg md:shadow-none md:flex"
  >
    <LuraCopilot
      :thread="thread"
      :active-run="activeRun"
      :conversation-id="conversationId"
      :conversation-inbox-type="conversationInboxType"
      @send-message="sendMessage"
      @decide="decide"
      @cancel="cancelRun"
      @reset="resetThread"
      @set-auto-approve="setAutoApprove"
    />
  </div>
  <template v-else />
</template>
